import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi, publicAssetUrl } from '../../lib/adminApi';

const statusClass = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-700',
};

export default function AdminDepartmentList() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  async function loadDepartments() {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      const data = await adminApi(`/api/admin/departments?${params.toString()}`);
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function changeStatus(id, nextStatus) {
    try {
      await adminApi(`/api/admin/departments/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      await loadDepartments();
    } catch (err) {
      alert(err.message);
    }
  }

  async function deleteDepartment(id) {
    if (!window.confirm('Delete this department permanently? This also deletes its programmes.')) return;
    try {
      await adminApi(`/api/admin/departments/${id}`, { method: 'DELETE' });
      setDepartments((items) => items.filter((item) => item.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <AdminLayout
      title="Department CMS"
      subtitle="Manage schools, departments, HOD profile, vision, mission and programmes from one dashboard."
      actions={
        <Link to="/adxm/departments/create" className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-black text-white shadow hover:bg-emerald-800">
          + Add Department
        </Link>
      }
    >
      <div className="mb-6 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_220px_auto]">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search department, school, HOD..."
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600"
        >
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button onClick={loadDepartments} className="rounded-xl border border-emerald-200 px-5 py-3 text-sm font-black text-emerald-700 hover:bg-emerald-50">
          Search
        </button>
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
      {loading && <div className="rounded-xl bg-white p-6 text-sm font-bold text-slate-600 shadow-sm">Loading departments...</div>}

      {!loading && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-4">Department</th>
                  <th className="px-5 py-4">School</th>
                  <th className="px-5 py-4">HOD</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {departments.map((item) => (
                  <tr key={item.id} className="align-top hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="flex gap-4">
                        <div className="h-16 w-20 flex-none overflow-hidden rounded-xl bg-slate-100">
                          {item.hero_image ? <img src={publicAssetUrl(item.hero_image)} alt="" className="h-full w-full object-cover" /> : null}
                        </div>
                        <div>
                          <div className="font-black text-slate-950">{item.name}</div>
                          <div className="mt-1 line-clamp-2 max-w-xl text-xs text-slate-600">{item.description}</div>
                          {item.slug && <Link to={`/departments/${item.slug}`} className="mt-2 inline-block text-xs font-black text-emerald-700">View public page</Link>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{item.school || '-'}</td>
                    <td className="px-5 py-4 text-slate-600">{item.hod_name || '-'}</td>
                    <td className="px-5 py-4"><span className={`rounded-full px-3 py-1 text-xs font-black ${statusClass[item.status] || statusClass.inactive}`}>{item.status}</span></td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Link to={`/adxm/departments/edit/${item.id}`} className="rounded-lg border border-slate-200 px-3 py-2 font-bold text-slate-700 hover:bg-slate-100">Edit</Link>
                        {item.status !== 'active' ? (
                          <button onClick={() => changeStatus(item.id, 'active')} className="rounded-lg border border-emerald-200 px-3 py-2 font-bold text-emerald-700 hover:bg-emerald-50">Activate</button>
                        ) : (
                          <button onClick={() => changeStatus(item.id, 'inactive')} className="rounded-lg border border-amber-200 px-3 py-2 font-bold text-amber-700 hover:bg-amber-50">Deactivate</button>
                        )}
                        <button onClick={() => deleteDepartment(item.id)} className="rounded-lg border border-red-200 px-3 py-2 font-bold text-red-700 hover:bg-red-50">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!departments.length && (
                  <tr>
                    <td colSpan="5" className="px-5 py-10 text-center text-sm font-bold text-slate-500">No departments found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
